import { useRef } from "react";
import { keyBy } from "lodash-es";
import { objectShallowEq } from "@/objectShallowEq";
import type { BlockDefinition, BlockId } from "@softr-io/blocks-sdk";

const EMPTY = {} as const;

/**
 * Create a map of all nested blocks for each container block.
 * Make sure that the child blocks are stable references otherwise every block
 * on the page will re-render if any single block changes!
 */
export function useNestedBlockMap(
    definitions: readonly BlockDefinition[],
): Record<BlockId, Record<BlockId, BlockDefinition>> {
    const byId = keyBy(definitions, "id");
    const ref = useRef<Record<BlockId, Record<BlockId, BlockDefinition>>>({});

    for (const def of Object.values(byId)) {
        if (def.type !== "container") {
            ref.current[def.id] = EMPTY;
            continue;
        }
        const oldNested = ref.current[def.id];
        const newNested = nestedBlocksOf(def, byId);

        if (!oldNested || !newNested || !objectShallowEq(newNested, oldNested)) {
            ref.current[def.id] = newNested;
        }
    }

    return ref.current;
}

/**
 * Recursively find all nested blocks for a given container block.
 * */
function nestedBlocksOf(
    definition: BlockDefinition,
    blocksById: Record<string, BlockDefinition>,
): Record<string, BlockDefinition> {
    if (definition.type !== "container") {
        return EMPTY;
    }
    let result = {};

    for (const slot of definition.container.slots) {
        if (!slot.blockId) {
            continue;
        }
        const block = blocksById[slot.blockId];
        const currentNestedBlocks = block ? nestedBlocksOf(block, blocksById) : EMPTY;

        result = { ...result, [slot.blockId]: block, ...currentNestedBlocks };
    }

    return result;
}
